// AGC: Badge - Status badge component for Classic Navy Theme
// AGC: Clean badges with navy and semantic colors
import React from 'react'

type BadgeVariant = 'default' | 'accent' | 'success' | 'warning' | 'error' | 'info'
type BadgeSize = 'sm' | 'md' | 'lg'

interface BadgeProps {
  children: React.ReactNode
  variant?: BadgeVariant
  size?: BadgeSize
  icon?: React.ReactNode
  className?: string
  /** AGC: Makes the badge look like a pill (more rounded) */
  pill?: boolean
  /** AGC: Adds a dot indicator before the text */
  dot?: boolean
}

/**
 * AGC: Badge - Clean status indicator for Classic Navy Theme
 * AGC: All variants designed for white/light backgrounds
 * AGC: Uses subtle background colors with darker text
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  icon,
  className = '',
  pill = false,
  dot = false,
}) => {
  // AGC: Variant styles for Classic Navy theme
  const variantClasses: Record<BadgeVariant, string> = {
    default: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300',
    accent: 'bg-navy-100 text-navy-700 dark:bg-navy-800 dark:text-navy-200',
    success: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
    warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
    info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  }

  // AGC: Dot color classes
  const dotClasses: Record<BadgeVariant, string> = {
    default: 'bg-gray-500',
    accent: 'bg-navy-600',
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
  }

  // AGC: Size styles
  const sizeClasses: Record<BadgeSize, string> = {
    sm: 'px-1.5 py-0.5 text-xs gap-1',
    md: 'px-2 py-0.5 text-xs gap-1.5',
    lg: 'px-2.5 py-1 text-sm gap-1.5',
  }

  const iconSizeClasses: Record<BadgeSize, string> = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
  }

  // AGC: Border radius
  const radiusClasses = pill ? 'rounded-full' : 'rounded-md'

  return (
    <span
      className={`
        inline-flex items-center font-medium
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${radiusClasses}
        ${className}
      `.replace(/\s+/g, ' ').trim()}
    >
      {/* AGC: Status dot */}
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full ${dotClasses[variant]}`} />
      )}
      
      {/* AGC: Icon */}
      {icon && (
        <span className={iconSizeClasses[size]}>{icon}</span>
      )}
      
      {children}
    </span>
  )
}

/**
 * AGC: StatusDot - Simple colored dot for inline status indication
 */
interface StatusDotProps {
  variant?: BadgeVariant
  size?: 'sm' | 'md' | 'lg'
  pulse?: boolean
  className?: string
}

export const StatusDot: React.FC<StatusDotProps> = ({
  variant = 'default',
  size = 'md',
  pulse = false,
  className = '',
}) => {
  // AGC: Colors for Classic Navy theme
  const colorClasses: Record<BadgeVariant, string> = {
    default: 'bg-gray-400',
    accent: 'bg-navy-600',
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
  }

  const sizeClasses: Record<'sm' | 'md' | 'lg', string> = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  }

  return (
    <span className={`relative inline-flex ${className}`}>
      <span
        className={`
          rounded-full
          ${colorClasses[variant]}
          ${sizeClasses[size]}
        `.replace(/\s+/g, ' ').trim()}
      />
      {pulse && (
        <span
          className={`
            absolute inline-flex rounded-full opacity-75 animate-ping
            ${colorClasses[variant]}
            ${sizeClasses[size]}
          `.replace(/\s+/g, ' ').trim()}
        />
      )}
    </span>
  )
}

export default Badge
